import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import { useTranslation } from "react-i18next";
import { axiosPrivate } from "../../../api/axios";

export default function CopyDtToSvDialog({ project, onClose, onResult }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("ready");
  const [confirmation, setConfirmation] = useState(null);
  const [message, setMessage] = useState("");
  const sending = useRef(false);
  const busy = phase === "copying";
  const done = phase === "done";
  const label = key => t(`pages.project.svCopy.${key}`);

  useEffect(() => {
    if (!busy) return undefined;
    const warnBeforeLeaving = event => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [busy]);

  const startCopy = async () => {
    if (sending.current || done) return;
    sending.current = true;
    setPhase("copying");
    setMessage("");
    try {
      const body = { project_id: Number(project.project_id) };
      if (confirmation) body.confirmation = confirmation.token;
      const { data } = await axiosPrivate.post("/project/copyDtToSv", body, { timeout: 180000 });
      if (data?.status === "done") {
        setConfirmation(null);
        setPhase("done");
        onResult?.({ severity: "success", message: label("success") });
      } else if (data?.status === "confirm" && typeof data.confirmation === "string" &&
        ["create", "replaceData", "recreate"].includes(data.mode)) {
        setConfirmation({ token: data.confirmation, mode: data.mode });
        setPhase("confirm");
      } else {
        setConfirmation(null);
        setMessage(label("unknownResult"));
        setPhase("error");
        onResult?.({ severity: "error", message: label("unknownResult") });
      }
    } catch (error) {
      // Never automatically replay a destructive request after a lost response.
      setConfirmation(null);
      const serverMessage = error.response?.data?.message;
      const failure = typeof serverMessage === "string"
        ? serverMessage.replace(/\bSV\b/g, "SimView").replace(/\bDT\b/g, label("digitalTwin"))
        : label("unknownResult");
      setMessage(failure);
      setPhase("error");
      onResult?.({ severity: "error", message: failure });
    } finally {
      sending.current = false;
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown={busy}
      onClose={() => { if (!sending.current) onClose(); }}
      aria-labelledby="sv-copy-title" aria-describedby="sv-copy-description">
      <DialogTitle id="sv-copy-title">{t("pages.project.copyDtToSv")}</DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {project.project_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          {project.group_code} / {project.project_code}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 2,
          alignItems: "center", textAlign: "center", py: 3 }}>
          <Box><Typography variant="caption" color="text.secondary">{label("source")}</Typography>
            <Typography variant="subtitle1">{label("digitalTwin")}</Typography></Box>
          <ArrowForwardIcon color="action" />
          <Box><Typography variant="caption" color="text.secondary">{label("target")}</Typography>
            <Typography variant="subtitle1">SimView</Typography></Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography id="sv-copy-description" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {label("scope")}
        </Typography>
        {phase === "ready" && <Alert severity="info">{label("ready")}</Alert>}
        {phase === "confirm" && <Alert severity="warning">
          {label(confirmation.mode === "recreate" ? "recreateWarning" :
            confirmation.mode === "replaceData" ? "overwriteWarning" : "changedWarning")}
        </Alert>}
        {busy && <Box role="status" aria-live="polite" sx={{ display: "flex", gap: 2, alignItems: "center", py: 1 }}>
          <CircularProgress size={24} aria-label={label("working")} sx={{ flexShrink: 0 }} />
          <Box><Typography variant="body2">{label("working")}</Typography>
            <Typography variant="caption" color="text.secondary">{label("wait")}</Typography></Box>
        </Box>}
        {done && <Alert severity="success">{label("success")}</Alert>}
        {phase === "error" && <Alert severity="error">{message}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
        <Button disabled={busy} onClick={onClose}>
          {done || phase === "error" ? label("close") : t("pages.project.cancel")}
        </Button>
        {!done && <Button variant="contained" disabled={busy}
          color={phase === "confirm" && confirmation.mode !== "create" ? "warning" : "primary"}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <FileCopyOutlinedIcon />}
          onClick={startCopy}>
          {busy ? label("workingShort") : phase === "confirm" && confirmation.mode !== "create"
            ? label("overwrite") : label("start")}
        </Button>}
      </DialogActions>
    </Dialog>
  );
}
